import { InfoIcon } from '@chakra-ui/icons';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightAddon,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { CheckCircleIcon, ExclamationCircleIcon, PlusIcon } from '@heroicons/react/outline';
import { PencilIcon } from '@heroicons/react/solid';
import { yupResolver } from '@hookform/resolvers/yup';
import { Select, SingleValue } from 'chakra-react-select';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { useTranslations } from 'use-intl';

import { FormErrors, nationalityOptions } from '@blockpulse3/data/shared';
import { DraftShareholder, UboRole } from '@blockpulse3/graphql/hooks';
import {
  alpha3CountryOptions,
  formatNumberInt,
  formatNumberPercentage,
} from '@blockpulse3/helpers';
import { ErrorMessage } from '@blockpulse3/ui/commons';
import { useCompanyShares } from '@blockpulse3/ui/ui-hooks';

import { CompanyOption, SearchCompanySelect } from '../../../../../SearchCompanySelect';
import { useSPVForm } from '../../../provider';
import {
  draftShareholderDefaultValues,
  legalShareholderSchema,
  schemaUbo,
} from '../../../provider/schema';
import { UpdatePreferredSharesPercentageModal } from '../UpdatePreferredSharesPercentagesModal';
import { ISharePercentageForm } from '../types';

const DEFAULT_PREFERRED_SHARES_PERCENTAGE = 50;

type Props = {
  /* ** Are fields disabled ** */
  isDisabled?: boolean;
  /* ** Default form values, Legal form version ** */
  defaultValues?: DraftShareholder;
  /* ** Callback of the Cancel button ** */
  onCancel: () => void;
  /* ** Callback of the Submit button ** */
  onSubmit: (data: DraftShareholder) => void;
};

/**
 * LegalShareholderInformationsForm.
 * Legal version of the ShareholderForm.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function LegalShareholderInformationsForm({
  isDisabled = false,
  defaultValues = draftShareholderDefaultValues,
  onCancel,
  onSubmit,
}: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nShareholderStatus = useTranslations('ShareholderStatusValues');
  const i18nLegalShareholderInformationsForm = useTranslations('LegalShareholderInformationsForm');

  const {
    companyDraftContent: { nominalValue, carriedInterest },
  } = useSPVForm();

  /* ** Flag to disable and enable the addition of an UBO ** */
  const [shouldAddUBO, setShouldAddUBO] = useState<boolean>(false);

  /* ** PreferredSharesPercentage field modal ** */
  const { isOpen, onOpen, onClose } = useDisclosure();

  /* ** Shareholder form ** */
  const { register, control, formState, watch, setValue, setError, handleSubmit, trigger } =
    useForm<DraftShareholder>({
      defaultValues,
      resolver: yupResolver(legalShareholderSchema),
      shouldFocusError: false,
    });

  /* ** Update fields on company name change ** */
  const handleNameChange = (option: SingleValue<CompanyOption>): void => {
    if (!option) {
      return;
    }
    /* ** Set fields with Pappers' results ** */
    setValue('name', option.name);
    setValue('registrationNumber', option.registrationNumber);
    setValue('registrationEntity', option.registrationEntity);
    setValue('address.line', option.address.line);
    setValue('address.city', option.address.city);
    setValue('address.postalCode', option.address.postalCode);
    setValue('address.country', option.address.country);
    trigger();
  };

  /* ** Update preferredSharesPercentage ** */
  const handleSharePercentageSubmit = (data: ISharePercentageForm): void => {
    setValue('preferredSharesPercentages', data.percentage);
    onClose();
  };

  /* ** Track UBO field array ** */
  const { fields, append, remove } = useFieldArray({ name: 'ubos', control });

  /* ** Watch for a minimum of one UBO ** */
  const isDefaultUBO = useMemo(() => fields.length === 1, [fields.length]);

  /* ** Watcher for the array of UBOs ** */
  const ubos = watch('ubos');
  const capitalDeposit = watch('capitalDeposit');
  const preferredSharesPercentage = watch('preferredSharesPercentages');

  /* ** Compute share counts ** */
  const { preferredSharesCount } = useCompanyShares({
    capitalDeposit,
    nominalValue,
    preferredSharesPercentage,
  });

  /* ** Check if we can add an ubo ** */
  const canAddUbo = useCallback(() => {
    const areUbosValid = ubos.every((ubo) => schemaUbo.isValidSync(ubo));
    const areUbosUnder75 = ubos.reduce((acc, ubo) => acc + ubo.sharePercentage * 1, 0) < 75;
    return areUbosValid && areUbosUnder75;
  }, [ubos]);

  /* ** Add an UBO to the form with useFieldArray, update the validation ** */
  const handleUBOAppend = (): void => {
    append(draftShareholderDefaultValues.ubos[0]);
    setShouldAddUBO(canAddUbo());
  };

  /* ** Remove an UBO to the form with useFieldArray, update the validation ** */
  const handleUBORemove = (index: number): void => {
    remove(index);
    setShouldAddUBO(canAddUbo());
  };

  const handleFormSubmit: SubmitHandler<DraftShareholder> = (data) => {
    if (preferredSharesPercentage && preferredSharesCount <= 0) {
      setError(
        'capitalDeposit',
        { type: 'custom', message: FormErrors.PreferredShareCount },
        { shouldFocus: true },
      );
    } else {
      onSubmit(data);
    }
  };

  const handleFormCancel = (): void => {
    onCancel();
  };

  useEffect(() => {
    /* ** Set preferredSharesPercentage to 5 by default if carried interest case is checked ** */
    if (carriedInterest > 0 && !preferredSharesPercentage) {
      setValue('preferredSharesPercentages', DEFAULT_PREFERRED_SHARES_PERCENTAGE);
    }
  }, [carriedInterest, preferredSharesPercentage, setValue]);

  /* ** Update button disable state, coming from a next step ** */
  useEffect(() => {
    if (ubos && ubos.length >= 1) {
      setShouldAddUBO(canAddUbo());
    }
  }, [ubos, formState, canAddUbo]);

  return (
    <>
      <UpdatePreferredSharesPercentageModal
        isOpen={isOpen}
        defaultValues={{
          percentage: preferredSharesPercentage || DEFAULT_PREFERRED_SHARES_PERCENTAGE,
        }}
        onClose={onClose}
        onSubmit={handleSharePercentageSubmit}
      />
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack spacing="8">
          <Stack spacing="4">
            <FormControl isInvalid={!!formState.errors?.capitalDeposit}>
              <FormLabel htmlFor="capitalDeposit">
                {i18nLegalShareholderInformationsForm('CapitalDepositField')}
              </FormLabel>
              <InputGroup>
                <Input
                  id="capitalDeposit"
                  type="number"
                  {...register('capitalDeposit')}
                  w={{ base: '40%' }}
                />
                <InputRightAddon>€</InputRightAddon>
                <Text marginLeft="3">
                  {preferredSharesPercentage && capitalDeposit
                    ? i18nLegalShareholderInformationsForm.rich('PreferredSharesPercentageField', {
                        preferredSharesPercentages:
                          formatNumberPercentage(preferredSharesPercentage),
                        preferredSharesCount: formatNumberInt(preferredSharesCount),
                        updatePreferredSharesPercentageLink:
                          i18nLegalShareholderInformationsForm('Modify'),
                        important: (chunks) => <b>{chunks}</b>,
                        link: (chunks) => (
                          <>
                            <button
                              onClick={(e): void => {
                                e.preventDefault();
                                onOpen();
                              }}
                            >
                              <u>
                                <b>{chunks}</b>
                              </u>
                              <Icon as={PencilIcon} color="primary" />
                            </button>
                            <br />
                          </>
                        ),
                      })
                    : null}
                </Text>
              </InputGroup>
              <ErrorMessage error={formState.errors?.capitalDeposit} />
            </FormControl>
            <FormControl>
              <FormLabel htmlFor="searchCompany">
                {i18nLegalShareholderInformationsForm('SearchCompanyField')}
              </FormLabel>
              <SearchCompanySelect
                isDisabled={isDisabled}
                menuPlacement="auto"
                placeholder={i18nLegalShareholderInformationsForm('SearchCompanyPlaceholder')}
                value={null}
                onChange={handleNameChange}
              />
            </FormControl>
            <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
              <FormControl isInvalid={!!formState.errors?.name}>
                <FormLabel htmlFor="name">
                  {i18nLegalShareholderInformationsForm('NameField')}
                </FormLabel>
                <Input id="name" isDisabled={isDisabled} type="text" {...register('name')} />
                <ErrorMessage error={formState.errors?.name} />
              </FormControl>
              <FormControl isInvalid={!!formState.errors?.creationDate}>
                <FormLabel htmlFor="creationDate">
                  {i18nLegalShareholderInformationsForm('CreationDateField')}
                </FormLabel>
                <Input
                  id="creationDate"
                  isDisabled={isDisabled}
                  type="date"
                  {...register('creationDate')}
                />
                <ErrorMessage error={formState.errors?.creationDate} />
              </FormControl>
            </Stack>
            <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
              <FormControl isInvalid={!!formState.errors?.corporateForm}>
                <HStack alignItems="flex-start" spacing="0">
                  <FormLabel htmlFor="corporateForm">
                    {i18nLegalShareholderInformationsForm('CorporateFormField')}
                  </FormLabel>
                </HStack>
                <Input
                  id="corporateForm"
                  isDisabled={isDisabled}
                  type="text"
                  {...register('corporateForm')}
                />
                <ErrorMessage error={formState.errors?.corporateForm} />
              </FormControl>
              <FormControl isInvalid={!!formState.errors?.shareCapital}>
                <HStack alignItems="flex-start" spacing="0">
                  <FormLabel htmlFor="shareCapital">
                    {i18nLegalShareholderInformationsForm('ShareCapitalField')}
                  </FormLabel>
                </HStack>
                <InputGroup>
                  <Input
                    id="shareCapital"
                    isDisabled={isDisabled}
                    step="0.01"
                    type="number"
                    {...register('shareCapital')}
                  />
                  <InputRightAddon>€</InputRightAddon>
                </InputGroup>
                <ErrorMessage error={formState.errors?.shareCapital} />
              </FormControl>
            </Stack>
            <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
              <FormControl isInvalid={!!formState.errors?.registrationNumber}>
                <HStack alignItems="flex-start" spacing="0">
                  <FormLabel htmlFor="registrationNumber">
                    {i18nLegalShareholderInformationsForm('IdentificationNumberField')}
                  </FormLabel>
                  <Tooltip
                    hasArrow
                    placement="top"
                    label={i18nLegalShareholderInformationsForm(
                      'Tooltip.IdentificationNumberField',
                    )}
                  >
                    <Icon as={InfoIcon} color="gray.500" />
                  </Tooltip>
                </HStack>
                <Input
                  id="registrationNumber"
                  isDisabled={isDisabled}
                  type="text"
                  {...register('registrationNumber')}
                />
                <ErrorMessage error={formState.errors?.registrationNumber} />
              </FormControl>
              <FormControl isInvalid={!!formState.errors?.registrationEntity}>
                <HStack alignItems="flex-start" spacing="0">
                  <FormLabel htmlFor="registrationEntity">
                    {i18nLegalShareholderInformationsForm('IdentificationEntityField')}
                  </FormLabel>
                </HStack>
                <Input
                  id="registrationEntity"
                  isDisabled={isDisabled}
                  type="text"
                  {...register('registrationEntity')}
                />
                <ErrorMessage error={formState.errors?.registrationEntity} />
              </FormControl>
            </Stack>
            <Stack bg="gray.50" borderRadius="md" p="3" spacing="2">
              <Text fontSize="lg" fontWeight="bold">
                {i18nLegalShareholderInformationsForm('Address.Title')}
              </Text>
              <Stack spacing="4">
                <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                  <FormControl isInvalid={!!formState.errors?.address?.line}>
                    <FormLabel htmlFor="line">
                      {i18nLegalShareholderInformationsForm('Address.LineField')}
                    </FormLabel>
                    <Input
                      id="line"
                      isDisabled={isDisabled}
                      type="string"
                      {...register('address.line')}
                    />
                    <ErrorMessage error={formState.errors?.address?.line} />
                  </FormControl>
                  <FormControl isInvalid={!!formState.errors?.address?.city}>
                    <FormLabel htmlFor="city">
                      {i18nLegalShareholderInformationsForm('Address.CityField')}
                    </FormLabel>
                    <Input
                      id="city"
                      isDisabled={isDisabled}
                      type="string"
                      {...register('address.city')}
                    />
                    <ErrorMessage error={formState.errors?.address?.city} />
                  </FormControl>
                </Stack>
                <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                  <FormControl isInvalid={!!formState.errors?.address?.postalCode}>
                    <FormLabel htmlFor="zipcode">
                      {i18nLegalShareholderInformationsForm('Address.ZipCodeField')}
                    </FormLabel>
                    <Input
                      id="zipcode"
                      isDisabled={isDisabled}
                      type="string"
                      {...register('address.postalCode')}
                    />
                    <ErrorMessage error={formState.errors?.address?.postalCode} />
                  </FormControl>
                  <Controller
                    control={control}
                    name="address.countryData"
                    render={({ field }): JSX.Element => (
                      <FormControl isInvalid={!!formState.errors.address?.countryData}>
                        <FormLabel htmlFor="country">
                          {i18nLegalShareholderInformationsForm('Address.CountryField')}
                        </FormLabel>
                        <Select
                          id="country"
                          isDisabled={isDisabled}
                          menuPlacement="auto"
                          options={alpha3CountryOptions}
                          {...field}
                        />
                        <ErrorMessage error={formState.errors.address?.countryData?.value} />
                      </FormControl>
                    )}
                  />
                </Stack>
              </Stack>
            </Stack>
            <Stack bg="gray.50" borderRadius="md" p="3" spacing="2">
              <Text fontSize="lg" fontWeight="bold">
                {i18nLegalShareholderInformationsForm('LastNaturalRepresentantTitle')}
              </Text>
              <Stack spacing="4">
                <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                  <FormControl isInvalid={!!formState.errors?.representative?.firstName}>
                    <FormLabel htmlFor="firstName">
                      {i18nLegalShareholderInformationsForm('FirstNameField')}
                    </FormLabel>
                    <Input
                      id="firstName"
                      isDisabled={isDisabled}
                      type="string"
                      {...register('representative.firstName')}
                    />
                    <ErrorMessage error={formState.errors?.representative?.firstName} />
                  </FormControl>
                  <FormControl isInvalid={!!formState.errors?.representative?.lastName}>
                    <FormLabel htmlFor="lastName">
                      {i18nLegalShareholderInformationsForm('LastNameField')}
                    </FormLabel>
                    <Input
                      isDisabled={isDisabled}
                      type="string"
                      {...register('representative.lastName')}
                    />
                    <ErrorMessage error={formState.errors?.representative?.lastName} />
                  </FormControl>
                </Stack>
                <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                  <FormControl isInvalid={!!formState.errors?.representative?.position}>
                    <FormLabel htmlFor="position">
                      {i18nLegalShareholderInformationsForm('RoleField')}
                    </FormLabel>
                    <Input
                      id="position"
                      isDisabled={isDisabled}
                      type="string"
                      {...register('representative.position')}
                    />
                    <ErrorMessage error={formState.errors?.representative?.position} />
                  </FormControl>
                  <FormControl isInvalid={!!formState.errors?.representative?.email}>
                    <FormLabel htmlFor="email">
                      {i18nLegalShareholderInformationsForm('EmailField')}
                    </FormLabel>
                    <Input
                      id="email"
                      isDisabled={isDisabled}
                      type="text"
                      {...register('representative.email')}
                    />
                    <ErrorMessage error={formState.errors?.representative?.email} />
                  </FormControl>
                </Stack>
              </Stack>
            </Stack>
            {fields.map((field, index) => (
              <Accordion key={field.id} allowMultiple defaultIndex={[0]}>
                <AccordionItem mt="0">
                  <AccordionButton>
                    {schemaUbo.isValidSync(ubos[index]) ? (
                      <Tooltip
                        hasArrow
                        label={i18nLegalShareholderInformationsForm('Tooltip.Success')}
                        placement="top"
                      >
                        <Icon as={CheckCircleIcon} boxSize="30" color="green.500" />
                      </Tooltip>
                    ) : (
                      <Tooltip
                        hasArrow
                        label={i18nLegalShareholderInformationsForm('Tooltip.Warning')}
                        placement="top"
                      >
                        <Icon as={ExclamationCircleIcon} boxSize="30" color="yellow.400" />
                      </Tooltip>
                    )}
                    <Box flex="1" p="2" textAlign="left">
                      <Heading size="lg">
                        {i18nLegalShareholderInformationsForm('OwnerTitle')}
                      </Heading>
                    </Box>
                    <AccordionIcon boxSize="8" />
                  </AccordionButton>
                  <AccordionPanel>
                    <Stack spacing="8">
                      <Stack key={field.id} spacing="4">
                        <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                          <FormControl isInvalid={!!formState.errors?.ubos?.[index]?.firstName}>
                            <FormLabel htmlFor="firstName">
                              {i18nLegalShareholderInformationsForm('FirstNameField')}
                            </FormLabel>
                            <Input
                              id="firstName"
                              type="text"
                              {...register(`ubos.${index}.firstName`)}
                            />
                            <ErrorMessage error={formState.errors?.ubos?.[index]?.firstName} />
                          </FormControl>
                          <FormControl isInvalid={!!formState.errors?.ubos?.[index]?.lastName}>
                            <FormLabel htmlFor="lastName">
                              {i18nLegalShareholderInformationsForm('LastNameField')}
                            </FormLabel>
                            <Input
                              id="lastName"
                              type="text"
                              {...register(`ubos.${index}.lastName`)}
                            />
                            <ErrorMessage error={formState.errors?.ubos?.[index]?.lastName} />
                          </FormControl>
                        </Stack>
                        <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                          <FormControl isInvalid={!!formState.errors?.ubos?.[index]?.birthdate}>
                            <FormLabel htmlFor="birthdate">
                              {i18nLegalShareholderInformationsForm('BirthdateField')}
                            </FormLabel>
                            <Input
                              id="birthdate"
                              type="date"
                              {...register(`ubos.${index}.birthdate`)}
                            />
                            <ErrorMessage error={formState.errors?.ubos?.[index]?.birthdate} />
                          </FormControl>
                          <FormControl isInvalid={!!formState.errors?.ubos?.[index]?.birthplace}>
                            <FormLabel htmlFor="birthplace">
                              {i18nLegalShareholderInformationsForm('BirthplaceField')}
                            </FormLabel>
                            <Input
                              id="birthplace"
                              type="text"
                              {...register(`ubos.${index}.birthplace`)}
                            />
                            <ErrorMessage error={formState.errors?.ubos?.[index]?.birthplace} />
                          </FormControl>
                        </Stack>
                        <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                          <FormControl
                            isInvalid={!!formState.errors?.ubos?.[index]?.birthCityPostalCode}
                          >
                            <FormLabel htmlFor="birthCityPostalCode">
                              {i18nLegalShareholderInformationsForm('BirthCityPostalCode')}
                            </FormLabel>
                            <Input
                              id="birthCityPostalCode"
                              type="string"
                              {...register(`ubos.${index}.birthCityPostalCode`)}
                            />
                            <ErrorMessage
                              error={formState.errors?.ubos?.[index]?.birthCityPostalCode}
                            />
                          </FormControl>
                          <Controller
                            control={control}
                            name={`ubos.${index}.birthCountryData`}
                            render={({ field }): JSX.Element => (
                              <FormControl
                                isInvalid={!!formState.errors?.ubos?.[index]?.birthCountryData}
                              >
                                <FormLabel htmlFor="country">
                                  {i18nLegalShareholderInformationsForm('BirthCountryField')}
                                </FormLabel>
                                <Select
                                  id="country"
                                  menuPlacement="auto"
                                  options={alpha3CountryOptions}
                                  {...field}
                                />
                                <ErrorMessage
                                  error={formState.errors?.ubos?.[index]?.birthCountryData?.value}
                                />
                              </FormControl>
                            )}
                          />
                        </Stack>
                        <Stack bg="gray.50" borderRadius="md" p="3" spacing="2">
                          <Text fontSize="lg" fontWeight="bold">
                            {i18nLegalShareholderInformationsForm('Address.Title')}
                          </Text>
                          <Stack spacing="4">
                            <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                              <FormControl
                                isInvalid={!!formState.errors?.ubos?.[index]?.address?.line}
                              >
                                <FormLabel htmlFor="line">
                                  {i18nLegalShareholderInformationsForm('Address.LineField')}
                                </FormLabel>
                                <Input
                                  id="line"
                                  type="string"
                                  {...register(`ubos.${index}.address.line`)}
                                />
                                <ErrorMessage
                                  error={formState.errors?.ubos?.[index]?.address?.line}
                                />
                              </FormControl>
                              <FormControl
                                isInvalid={!!formState.errors?.ubos?.[index]?.address?.city}
                              >
                                <FormLabel htmlFor="city">
                                  {i18nLegalShareholderInformationsForm('Address.CityField')}
                                </FormLabel>
                                <Input
                                  id="city"
                                  type="string"
                                  {...register(`ubos.${index}.address.city`)}
                                />
                                <ErrorMessage
                                  error={formState.errors?.ubos?.[index]?.address?.city}
                                />
                              </FormControl>
                            </Stack>
                            <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                              <FormControl
                                isInvalid={!!formState.errors?.ubos?.[index]?.address?.postalCode}
                              >
                                <FormLabel htmlFor="zipcode">
                                  {i18nLegalShareholderInformationsForm('Address.ZipCodeField')}
                                </FormLabel>
                                <Input
                                  id="zipcode"
                                  type="string"
                                  {...register(`ubos.${index}.address.postalCode`)}
                                />
                                <ErrorMessage
                                  error={formState.errors?.ubos?.[index]?.address?.postalCode}
                                />
                              </FormControl>
                              <Controller
                                control={control}
                                name={`ubos.${index}.address.countryData`}
                                render={({ field }): JSX.Element => (
                                  <FormControl
                                    isInvalid={
                                      !!formState.errors?.ubos?.[index]?.address?.countryData
                                    }
                                  >
                                    <FormLabel htmlFor="country">
                                      {i18nLegalShareholderInformationsForm('Address.CountryField')}
                                    </FormLabel>
                                    <Select
                                      id="country"
                                      menuPlacement="auto"
                                      options={alpha3CountryOptions}
                                      {...field}
                                    />
                                    <ErrorMessage
                                      error={
                                        formState.errors?.ubos?.[index]?.address?.countryData?.value
                                      }
                                    />
                                  </FormControl>
                                )}
                              />
                            </Stack>
                          </Stack>
                        </Stack>
                        <Stack
                          direction={{ base: 'column', md: 'row' }}
                          spacing="4"
                          width={{ base: 'full', md: '50%' }}
                        >
                          <Controller
                            control={control}
                            name={`ubos.${index}.nationalityData`}
                            render={({ field }): JSX.Element => (
                              <FormControl
                                isInvalid={!!formState.errors?.ubos?.[index]?.nationalityData}
                              >
                                <FormLabel htmlFor="nationalityData">
                                  {i18nLegalShareholderInformationsForm('NationalityField')}
                                </FormLabel>
                                <Select
                                  id="nationalityData"
                                  menuPlacement="auto"
                                  options={nationalityOptions}
                                  {...field}
                                />
                                <ErrorMessage
                                  error={formState.errors?.ubos?.[index]?.nationalityData?.value}
                                />
                              </FormControl>
                            )}
                          />
                        </Stack>
                        <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                          <Controller
                            control={control}
                            name={`ubos.${index}.role`}
                            render={({ field }): JSX.Element => (
                              <FormControl isInvalid={!!formState.errors?.ubos?.[index]?.role}>
                                <FormLabel htmlFor="role">
                                  {i18nLegalShareholderInformationsForm('ShareholderStatusField')}
                                </FormLabel>
                                <RadioGroup id="role" {...field}>
                                  <Stack>
                                    {Object.keys(UboRole).map((role) => (
                                      <Radio key={role} value={role}>
                                        {i18nShareholderStatus(
                                          role as keyof IntlMessages['ShareholderStatusValues'],
                                        )}
                                      </Radio>
                                    ))}
                                  </Stack>
                                </RadioGroup>
                                <ErrorMessage error={formState.errors?.ubos?.[index]?.role} />
                              </FormControl>
                            )}
                          />
                        </Stack>
                        <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                          <FormControl
                            isInvalid={!!formState.errors?.ubos?.[index]?.sharePercentage}
                          >
                            <FormLabel htmlFor="percentage">
                              {i18nLegalShareholderInformationsForm('PercentageField')}
                            </FormLabel>
                            <InputGroup>
                              <Input
                                id="percentage"
                                step="0.01"
                                type="number"
                                {...register(`ubos.${index}.sharePercentage`)}
                              />
                              <InputRightAddon>%</InputRightAddon>
                            </InputGroup>
                            <ErrorMessage
                              error={formState.errors?.ubos?.[index]?.sharePercentage}
                            />
                          </FormControl>
                        </Stack>
                        <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                          <FormControl>
                            <Checkbox {...register(`ubos.${index}.isDirect`)}>
                              <Text fontSize="md">
                                {i18nLegalShareholderInformationsForm('IsDirectField')}
                              </Text>
                            </Checkbox>
                          </FormControl>
                        </Stack>
                      </Stack>
                      <HStack justifyContent="flex-end" spacing="4">
                        <Button
                          alignSelf="flex-end"
                          disabled={isDefaultUBO}
                          variant="secondary"
                          onClick={(): void => handleUBORemove(index)}
                        >
                          {i18nGeneral('Delete')}
                        </Button>
                      </HStack>
                    </Stack>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            ))}
            <Button
              disabled={!shouldAddUBO}
              fontSize="3xl"
              justifyContent="start"
              leftIcon={<PlusIcon height="25" width="25" />}
              variant="container"
              onClick={handleUBOAppend}
            >
              {i18nLegalShareholderInformationsForm('AddOwner')}
            </Button>
          </Stack>
          <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
            <Button type="button" variant="secondary" w="full" onClick={handleFormCancel}>
              {i18nGeneral('Back')}
            </Button>
            <Button type="submit" w="full">
              {i18nGeneral('Submit')}
            </Button>
          </Stack>
        </Stack>
      </form>
    </>
  );
}

export type LegalShareholderInformationsFormProps = Props;
